import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import React, { memo, useEffect, useRef } from 'react';
import { Portal } from 'react-portal';
import { type PopoverProps } from './Popover.types';
// eslint-disable-next-line
// @ts-ignore
import CSSEscape from 'css.escape';
import memoize from 'memoize-one';
import styled from 'styled-components';
import { Transition } from '../../mixins';
import { COLOR, fieldBorderRadius, SIZE } from '../../styles';

const Popover: React.FC<PopoverProps> = memo(
  ({
    onClose,
    animateHorizontalOffset = 0,
    animateVerticalOffset = 0,
    horizontalOffset = 0,
    horizontalOffsetToMiddle = false,
    horizontalOffsetToStart = false,
    horizontalPosition = 'right',
    isOpen = false,
    parent,
    sourcePoint,
    verticalOffset = 0,
    verticalOffsetToMiddle = false,
    verticalPosition = 'bottom',
    children = <div />,
    className,
    zIndex,
    disableOverlay = false,
    isOverlayVisible = true,
    onMouseEnter,
    onMouseLeave,
  }) => {
    const popoverRef = useRef(null);
    useEffect(() => document.removeEventListener('keydown', closeOnEsc, true));

    // eslint-disable-next-line
    // @ts-ignore
    const closeOnEsc = (event) => {
      if (event.keyCode === 27) {
        event.stopPropagation();
        // eslint-disable-next-line
        // @ts-ignore
        onClose?.();
      }
    };

    /**
     * Returns the position of the popover element.
     */
    const getAsidePosition = () => {
      return getAsidePositionHelper(
        animateHorizontalOffset,
        animateVerticalOffset,
        horizontalOffset,
        horizontalOffsetToMiddle,
        horizontalOffsetToStart,
        horizontalPosition,
        isOpen,
        parent,
        sourcePoint,
        verticalOffset,
        verticalOffsetToMiddle,
        verticalPosition,
        // eslint-disable-next-line
        // @ts-ignore
        popoverRef.current?.offsetWidth,
        // eslint-disable-next-line
        // @ts-ignore
        popoverRef.current?.offsetHeight,
      );
    };

    /**
     * This is a helper function for `getAsidePosition()` which computes the
     * actual position.  This function is memoized, so if we call it with the
     * same values, it'll skip the computation, so we can call this as
     * often as we want.
     */
    const getAsidePositionHelper = memoize(
      (
        animateHorizontalOffset,
        animateVerticalOffset,
        horizontalOffset,
        horizontalOffsetToMiddle,
        horizontalOffsetToStart,
        horizontalPosition,
        isOpen,
        parent,
        sourcePoint,
        verticalOffset,
        verticalOffsetToMiddle,
        verticalPosition,
        popoverOffsetWidth,
        popoverOffsetHeight,
      ) => {
        if (!parent && !!sourcePoint) {
          return { ...sourcePoint, transform: '' };
        }

        let target;
        if (typeof parent === 'string') {
          const parentIdPortion = parent.split('#')[1];
          target = document.querySelector('#' + CSSEscape(parentIdPortion));
        } else {
          target = parent;
        }

        if (!target) {
          return null;
        }

        const targetPosition = target.getBoundingClientRect();
        const position = { left: 0, top: 0, transform: '' };
        let separator = '';

        position.left =
          horizontalPosition === 'right' ? targetPosition.right + 1 : targetPosition.left - 1;
        position.top =
          verticalPosition === 'bottom' ? targetPosition.bottom + 1 : targetPosition.top - 1;

        // Apply offsets
        if (horizontalOffsetToMiddle || horizontalOffsetToStart) {
          const leftModifier = horizontalPosition === 'right' ? -1 : 1;
          position.left +=
            (leftModifier * targetPosition.width) / (horizontalOffsetToMiddle ? 2 : 1);
        }
        if (verticalOffsetToMiddle) {
          const topModifier = verticalPosition === 'bottom' ? -1 : 1;
          position.top += (topModifier * targetPosition.height) / 2;
        }
        position.left += horizontalOffset;
        position.top += verticalOffset;

        // Handle first render, and avoid cutoffs
        if (!popoverOffsetWidth || !popoverOffsetHeight) {
          if (horizontalPosition === 'center') {
            position.transform = 'translateX(-50%)';
            separator = ', ';
          } else if (horizontalPosition === 'left') {
            position.transform = 'translateX(-100%)';
            separator = ', ';
          }
          if (verticalPosition === 'center') {
            position.transform += `${separator}translateY(-50%)`;
          } else if (verticalPosition === 'top') {
            position.transform += `${separator}translateY(-100%)`;
          }
        } else {
          // Once we've rendered once, make sure we're not cutoff
          const maxLeft = window.innerWidth - popoverOffsetWidth;
          const maxTop = window.innerHeight - popoverOffsetHeight;

          if (horizontalPosition === 'center') {
            position.left -= popoverOffsetWidth / 2;
          } else if (horizontalPosition === 'left') {
            position.left -= popoverOffsetWidth;
          }
          if (verticalPosition === 'center') {
            position.top -= popoverOffsetHeight / 2;
          } else if (verticalPosition === 'top') {
            position.top -= popoverOffsetHeight;
          }

          if (position.left < 0) {
            // Prevent cutoff on the left
            position.left = 0;
          } else if (position.left > maxLeft) {
            // Prevent cutoff on the right
            position.left = maxLeft;
          }
          if (position.top < 0) {
            // Prevent cutoff on the top
            position.top = 0;
          } else if (position.top > maxTop) {
            // Prevent cutoff on the bottom
            position.top = maxTop;
          }

          // Handle animated offsets
          if (animateHorizontalOffset || animateVerticalOffset) {
            let left = animateHorizontalOffset;
            let top = animateVerticalOffset;
            const leftPos = position.left + animateHorizontalOffset;
            const topPos = position.top + animateVerticalOffset;

            if (leftPos < 0) {
              // Prevent cutoff on the left
              left = 0 - position.left;
            } else if (leftPos > maxLeft) {
              // Prevent cutoff on the right
              left = maxLeft - position.left;
            }
            if (topPos < 0) {
              // Prevent cutoff on the top
              top = 0 - position.top;
            } else if (topPos > maxTop) {
              // Prevent cutoff on the bottom
              top = maxTop - position.top;
            }

            position.transform = isOpen ? `translate3d(${left}px, ${top}px, 0)` : 'none';
          }
        }

        if (isEmpty(position.transform)) {
          // eslint-disable-next-line
          // @ts-ignore
          delete position.transform;
        }
        return position;
      },
    );
    // eslint-disable-next-line
    // @ts-ignore
    const trapClick = (event) => event.stopPropagation();

    const position = getAsidePosition();

    const isClosed = !isOpen;
    const wrapperClass = classNames('popover-wrapper', className, {
      'click-through': disableOverlay || isClosed,
      'invisible-background': disableOverlay || !isOverlayVisible || isClosed,
      open: isOpen,
      closed: isClosed,
    });

    return (
      <Portal>
        <PopoverWrapper className={wrapperClass} onClick={onClose} style={{ zIndex }}>
          <PopoverElement
            className="popover"
            onClick={trapClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            ref={popoverRef}
            style={position}
          >
            {children}
          </PopoverElement>
        </PopoverWrapper>
      </Portal>
    );
  },
);

// Styled Components
const PopoverWrapper = styled.section`
  background: ${COLOR['modal-overlay']};
  cursor: pointer;
  height: 100vh;
  left: 0;
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: 11000;

  &.click-through {
    pointer-events: none;
  }

  &.invisible-background {
    background: none;
  }

  &.closed {
    &,
    .popover {
      pointer-events: none;
    }

    .popover {
      ${Transition(
        'transform 300ms cubic-bezier(0.23, 1, 0.32, 1) 0ms, opacity 300ms cubic-bezier(0.23, 1, 0.32, 1) 0ms',
      )}
      opacity: 0;
    }
  }

  &.open {
    .popover {
      ${Transition(
        'transform 800ms cubic-bezier(0.23, 1, 0.32, 1) 0ms, opacity 300ms cubic-bezier(0.23, 1, 0.32, 1) 100ms',
      )}
      opacity: 1;
    }
  }

  &.table-customizer-selector {
    .popover {
      background: ${COLOR['white']};
      border: 1px solid ${COLOR['light-gray-light']};
      border-radius: ${fieldBorderRadius};
      max-width: 60vw;
      min-width: 480px;
      padding: ${SIZE['12']} ${SIZE['8']} ${SIZE['8']};
      position: relative;

      .table-customizer-selector-closer {
        z-index: 1;
        margin: 0;
        padding: ${SIZE['4']};
        position: absolute;
        right: ${SIZE['8']};
        top: ${SIZE['8']};
      }
    }
  }
`;

// Styled Components
const PopoverElement = styled.aside`
  box-shadow: rgba(0, 0, 0, 0.175) 0px 5px 10px;
  cursor: auto;
  left: 0;
  opacity: 0;
  position: fixed;
  top: 0;

  > * {
    margin: 0;
  }
`;

// Display Name
Popover.displayName = 'Popover';

export default Popover;
